<script setup>
import axios from "axios"
import { onMounted, ref } from "vue";

const request_types_kinds = ref([]);
const createNewType = ref(false);
const showError = ref(false);
const errorMessage = ref('');
const newRequestType = ref('');
const newRequestKind = ref('');
const kindsInput = ref({});
const wasValidated = ref(false);
const activeCollapse = ref({}); // Track which collapses are open

const fetchStatusesAndKinds = async () => {
  try {
    const res = await axios.get('request_types_with_kinds/');
    request_types_kinds.value = res.data.map(item => ({
      type_id: item.type_id,
      type_name: item.type_name,
      kinds: item.kinds.map(kind => ({
        kind_id: kind.kind_id,
        kind_name: kind.kind_name,
      }))
    }));
  } catch (error) {
    showError.value = true;
    errorMessage.value = 'Не удалось загрузить типы и виды заявок';
    setTimeout(() => showError.value = false, 3000);
  }
};

const postNewRequestTypeAndKind = async () => {
  wasValidated.value = true;

  if (!newRequestType.value || !newRequestKind.value) {
    return;
  }

  // Check if type already exists
  if (request_types_kinds.value.some(type => type.type_name === newRequestType.value)) {
    errorMessage.value = 'Такой тип уже существует!';
    showError.value = true;
    setTimeout(() => showError.value = false, 3000);
    return;
  }

  try {
    await axios.post('request_type/', {
      request_type_name: newRequestType.value,
      request_kind_name: newRequestKind.value,
    });
    await fetchStatusesAndKinds();
    createNewType.value = false;
    newRequestType.value = '';
    newRequestKind.value = '';
    wasValidated.value = false;
  } catch (error) {
    errorMessage.value = 'Не удалось создать новый тип заявки.';
    showError.value = true;
    setTimeout(() => showError.value = false, 3000);
  }
};

const postNewRequestKind = async (requestTypeId, requestKind) => {
  if (!requestKind) return;

  try {
    await axios.post('request_types_with_kinds/', {
      type_id: requestTypeId,
      kind_name: requestKind
    });
    await fetchStatusesAndKinds();
    kindsInput.value[requestTypeId] = ''; // Clear the input after successful submission
  } catch (error) {
    errorMessage.value = 'Не удалось создать новый вид заявки.';
    showError.value = true;
    setTimeout(() => showError.value = false, 3000);
  }
};

// Toggle collapse state
const toggleCollapse = (typeId, section) => {
  if (!activeCollapse.value[typeId]) {
    activeCollapse.value[typeId] = {};
  }
  activeCollapse.value[typeId][section] = !activeCollapse.value[typeId][section];
};

onMounted(() => {
  fetchStatusesAndKinds();
});
</script>

<template>
  <div class="content-wrapper">
    <div class="container-fluid" style="margin-top: 9vh">
      <!-- Error Alert -->
      <div v-if="showError" class="alert alert-danger alert-dismissible fade show" role="alert">
        {{ errorMessage }}
        <button type="button" class="btn-close" @click="showError = false" aria-label="Close"></button>
      </div>

      <div class="mx-3">
        <!-- Button to trigger new request type creation -->
        <button class="btn btn-primary my-3 shadow-sm" @click="createNewType = !createNewType">
          <i class="bi bi-plus-circle me-2"></i>
          {{ createNewType ? 'Отменить' : 'Новый тип заявки' }}
        </button>

        <!-- New Request Type Form -->
        <transition name="slide-fade">
          <div v-if="createNewType" class="card mb-4 shadow-sm">
            <div class="card-header bg-light">
              <h5 class="card-title mb-0">Создание нового типа заявки</h5>
            </div>
            <div class="card-body">
              <form @submit.prevent="postNewRequestTypeAndKind" novalidate>
                <!-- Request Type Input -->
                <div class="mb-3">
                  <label for="newRequestType" class="form-label">Новый тип заявки:</label>
                  <input
                      id="newRequestType"
                      :class="['form-control', { 'is-invalid': !newRequestType && wasValidated }]"
                      type="text"
                      v-model="newRequestType"
                      required
                  />
                  <div v-if="!newRequestType && wasValidated" class="invalid-feedback">
                    Пожалуйста, укажите тип заявки.
                  </div>
                </div>

                <!-- Request Kind Input -->
                <div class="mb-3">
                  <label for="newRequestKind" class="form-label">Новый вид заявки:</label>
                  <input
                      id="newRequestKind"
                      :class="['form-control', { 'is-invalid': !newRequestKind && wasValidated }]"
                      type="text"
                      v-model="newRequestKind"
                      required
                  />
                  <div v-if="!newRequestKind && wasValidated" class="invalid-feedback">
                    Пожалуйста, укажите вид заявки.
                  </div>
                </div>

                <!-- Form Buttons -->
                <div class="d-flex justify-content-end gap-2">
                  <button
                      type="button"
                      class="btn btn-outline-secondary"
                      @click="createNewType = false"
                  >
                    <i class="bi bi-x-lg me-2"></i>Отмена
                  </button>
                  <button
                      type="submit"
                      class="btn btn-success"
                      :disabled="!newRequestType || !newRequestKind"
                  >
                    <i class="bi bi-check-lg me-2"></i>Сохранить
                  </button>
                </div>
              </form>
            </div>
          </div>
        </transition>

        <!-- List of Request Types -->
        <div
            class="card mb-3 shadow-sm"
            v-for="request_type in request_types_kinds"
            :key="request_type.type_id"
        >
          <div class="card-header bg-light d-flex align-items-center">
            <!-- Collapse Toggle Button -->
            <button
                class="btn btn-link text-decoration-none me-3"
                @click="toggleCollapse(request_type.type_id, 'kinds')"
            >
              <i
                  class="fas"
                  :class="activeCollapse[request_type.type_id]?.kinds ? 'fa-chevron-up' : 'fa-chevron-down'"
              ></i>
            </button>

            <h5 class="card-title mb-0 me-auto">{{ request_type.type_name }}</h5>

            <!-- Add New Kind Button -->
            <button
                class="btn btn-outline-primary btn-sm"
                @click="toggleCollapse(request_type.type_id, 'create')"
            >
              <i class="fas fa-plus me-2"></i>
              {{ activeCollapse[request_type.type_id]?.create ? 'Отменить' : 'Добавить вид' }}
            </button>
          </div>

          <!-- New Request Kind Form -->
          <div
              class="collapse"
              :class="{ 'show': activeCollapse[request_type.type_id]?.create }"
          >
            <div class="card-body border-top">
              <div class="d-flex align-items-center gap-3">
                <input
                    class="form-control"
                    type="text"
                    v-model="kindsInput[request_type.type_id]"
                    placeholder="Введите новый вид заявки"
                    required
                />
                <button
                    class="btn btn-success btn-sm"
                    :disabled="!kindsInput[request_type.type_id]"
                    @click="postNewRequestKind(request_type.type_id, kindsInput[request_type.type_id])"
                >
                  <i class="bi bi-check-lg"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- List of Request Kinds -->
          <div
              class="collapse"
              :class="{ 'show': activeCollapse[request_type.type_id]?.kinds }"
          >
            <ul class="list-group list-group-flush">
              <li
                  class="list-group-item d-flex justify-content-between align-items-center"
                  v-for="kind in request_type.kinds"
                  :key="kind.kind_id"
              >
                {{ kind.kind_name }}
              </li>
              <li
                  v-if="request_type.kinds.length === 0"
                  class="list-group-item text-muted"
              >
                Нет видов заявок
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Card hover effect */
.card:hover {
  transform: translateY(-2px);
  transition: transform 0.2s ease;
}

/* Button hover effect */
.btn-outline-primary:hover {
  background-color: var(--bs-primary);
  color: white;
}

.collapse {
  transition: height 0.3s ease;
  overflow: hidden;
}

/* Make sure chevron icons have consistent width */
.fas {
  width: 1em;
  text-align: center;
}
</style>